import { createFilter } from 'react-select';

/**
 * One entity a set-membership condition can name, with the group it sits under.
 *
 * `group` is the group's `external_id`, joining to a `MultiSelectGroup`'s
 * `value` — not the group's name. The catalog keys the join by id so a rename
 * cannot split the two lists against each other, and two groups sharing a name
 * stay distinct.
 *
 * A null `group` does not mean "ungrouped". Every location has a group in the
 * database; the catalog withholds one when the option's *name* spans more than
 * one, when the group is soft-deleted, and for the boundary name of a capped
 * read. In each case the heading is unknown rather than absent, so the option
 * belongs under no header instead of under a header this list would guess.
 */
export interface MultiSelectOption {
  value: string;
  label: string;
  group?: string | null;
}

/** One group header's own identity, keyed the way an option's `group` names it. */
export interface MultiSelectGroup {
  value: string;
  label: string;
}

/** An entity the condition can hold. */
export interface OptionRow {
  kind: 'option';
  value: string;
  label: string;
  /** Whether it sits under a header, which is what the indent says. */
  indented: boolean;
  /** A chosen value the admin cannot remove; see `lockedValues`. */
  locked?: boolean;
}

/**
 * A group header, which is an option row rather than a heading beside them.
 *
 * react-select's own grouping renders a heading `div` with no place in the
 * menu's keyboard order, so a header built that way is reachable with a pointer
 * and with nothing else. As a row it takes its turn under the arrow keys, Enter
 * activates it, and react-select's live region announces it like any other
 * option.
 *
 * `members` is the group's whole membership. What the header stands for at any
 * moment is the part of it the search left on screen — `visibleMembers` — and
 * that is derived rather than stored, so a keystroke changes no row.
 */
export interface GroupRow {
  kind: 'group';
  value: string;
  label: string;
  members: OptionRow[];
}

export type MultiSelectRow = OptionRow | GroupRow;

/**
 * Namespaces a header's `value`, which react-select uses as the row's identity.
 *
 * A header is never stored, so the prefix is invisible to the condition. It is
 * here so that a header and an entity named the same thing stay two rows.
 */
export const GROUP_ROW_PREFIX = 'group:';

/**
 * react-select's own filter, so the grouped list narrows exactly as the flat
 * ones do — same case folding, same accent folding, same `label value` haystack.
 * Grouping means this control filters its own list and turns react-select's
 * filter off, and a second implementation would drift from the two pickers
 * beside it.
 */
const matchesSearch = createFilter(null);

const matches = (row: OptionRow, search: string) =>
  matchesSearch({ label: row.label, value: row.value, data: row }, search);

/**
 * Every row the menu holds, headers interleaved above their members.
 *
 * Built from the options alone. The search is applied by `rowMatches` at render
 * rather than here, so typing produces the same row objects it did on the
 * keystroke before — react-select tracks the focused row by object identity
 * (`getNextFocusedOption` calls `indexOf`), so a list rebuilt per keystroke
 * sends focus back to the first row on every character. A grouped menu is
 * exactly where an admin types and arrows in the same breath.
 *
 * Headers come from joining each option's `group` to `groups`, so a group with
 * no member gets no row — including a group holding no locations, which is still
 * a legal scope and is chosen from its own picker instead. An option whose group
 * is not in `groups` is treated as unknown rather than dropped: the group list is
 * capped like every other, and a location outside that cap is still a value the
 * condition can name.
 *
 * Options with no known header come first. Under any header they would read as
 * that group's members, and no header of their own can be written without naming
 * a group the catalog declined to name.
 */
export const buildRows = (
  options: MultiSelectOption[],
  groups: MultiSelectGroup[],
): MultiSelectRow[] => {
  const headerLabels = new Map(groups.map(group => [group.value, group.label]));

  const ungrouped: OptionRow[] = [];
  const members = new Map<string, OptionRow[]>();

  options.forEach(option => {
    const row: OptionRow = {
      kind: 'option',
      value: option.value,
      label: option.label,
      indented: false,
    };

    const header = option.group;
    if (!header || !headerLabels.has(header)) {
      ungrouped.push(row);
      return;
    }

    const held = members.get(header) ?? [];
    // One row per value. A condition is a set membership test, so a second row
    // for a name already listed offers nothing new to pick and would put that
    // name into the header's bulk toggle twice — a duplicate this control wrote
    // itself, into a value it promises to keep a set.
    if (held.some(member => member.value === row.value)) return;

    members.set(header, [...held, { ...row, indented: true }]);
  });

  const grouped = groups.flatMap<MultiSelectRow>(group => {
    const rows = members.get(group.value);
    if (!rows?.length) return [];

    return [
      {
        kind: 'group',
        value: `${GROUP_ROW_PREFIX}${group.value}`,
        label: group.label,
        members: rows,
      },
      ...rows,
    ];
  });

  return [...ungrouped, ...grouped];
};

/**
 * Whether the search leaves this row on screen.
 *
 * A header shows while any of its members does. Its own name is not searched —
 * a group is found by name in the group picker beside this one, and matching it
 * here would be a second way to do one thing.
 */
export const rowMatches = (row: MultiSelectRow, search: string): boolean =>
  row.kind === 'group'
    ? row.members.some(member => matches(member, search))
    : matches(row, search);

/**
 * What a header stands for right now: the members the search left on screen.
 *
 * The count beside the name, the state of its checkbox and the set a click
 * toggles all read this one list, so a search that hides half a group cannot
 * leave the header describing the other half.
 */
export const visibleMembers = (row: GroupRow, search: string): OptionRow[] =>
  row.members.filter(member => matches(member, search));

/**
 * What a header toggle leaves selected.
 *
 * Selecting is additive and deselecting is subtractive over the header's own
 * members, so anything else the condition holds — another group's members, a
 * value the catalog no longer lists — keeps its place and its order.
 *
 * "All of them are already selected" is what makes it a deselect. A header
 * standing for three locations with two selected fills the third rather than
 * emptying the group, which is what its partial state says it will do.
 */
export const toggleGroupMembers = (
  values: string[],
  members: string[],
): string[] => {
  const held = new Set(values);

  if (members.every(member => held.has(member))) {
    const dropped = new Set(members);

    return values.filter(value => !dropped.has(value));
  }

  return [...values, ...members.filter(member => !held.has(member))];
};

/** How much of a header's visible membership the condition holds. */
export const groupSelectionCount = (
  members: OptionRow[],
  values: Set<string>,
): number => members.filter(member => values.has(member.value)).length;
